'use strict';
/* ==========================================================================
   levels/mic.js — step "mic" (any): OPEN MIC, the spoken-word piece "Like a Simile, Only Sneakier" performed by Nia
   on the school's open-mic stage, with zine karaoke (DESIGN.md §2, §5.4; content/mic.json).

   ► The piece: assets/music/mic.m4a = ACE-Step take spokenword_alt_7 (spoken word over a lo-fi beat, 86 s;
     assets/ASSETS.md, CHOICE.md). Timings: assets/music/mic_words.json (tools/song_timing.py, rev 2: no Bridge, 19 lines,
     none interpolated): the TRUE lines of content/mic.json with word times from the take's Whisper passes, and the beat
     grid {bpm 85, offset = first downbeat ≈ 2.6 s}.

   The whole frame is a pure function of SONG TIME t (the shared <audio> element's clock while it plays; a wall clock
   while audio is locked or missing), so seeking, pausing and the QA screenshots are exact. Every picture beat is keyed
   to a spoken word (S.W(line, word)); the device colours are the model's (Devices.colour).

   The stage (always on): a dark club with red curtains, the stage boards and their light (Zine.stage), the spotlight
   lamp on its tripod at stage left, Nia at a desk mic on a tall table (she bobs while she speaks, points at the board
   when a device is named), Ms. Park hosting at the right (pen raised at the end), finger-snap doodles on the backbeat.
   A photocopied page taped up behind them (the board) shows what each line does, keyed to its words:
     intro    0      OPEN MIC card; the piece's title types on; the typewriter
     simile   1–2    "as loud as a stadium" in yellow, SIMILE chip; HALLWAY ≈ STADIUM (two wavy lines, never a glyph)
     notsim   3–4    "I like pizza": the "like" struck out in red, NOT A SIMILE chip, "just a verb"; LOOK FOR: two things
     chorus   5–8, 13–16   "Like a simile, only sneakier", METAPHOR chip; LIKE and AS struck out, IS in pink; then the hook
                     "Not a lie with extra steps / (It's) the truth in better shoes" as big cut-out (ransom) letters
     hyper    9–10   "weighs a ton" in orange, a 1 TON weight drops, HYPERBOLE chip, BIG on purpose
     person   11     "wind whispered" in green, the cloud gets a face, PERSONIFICATION chip
     idiom    12     "Break a leg" in teal = good luck!, IDIOM chip
     outro    17–18  the hook once more, then PUBLISHED (+ snaps)
   Karaoke (bottom band, a photocopied strip): the current line in typewriter type, each word STAMPS on as it is spoken
   (unspoken words are faint), the spoken word in red; each device's highlighter swipes under its words as they're said
   (MARKS below: Devices.colour); the "like" of "I like pizza" is struck out; the hook lines stamp on as cut-out letters;
   the device's chip at the band's right; the next line faint below.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom;
   "Finish" once ≥ 90 % has been heard. The piece's end (≥ 90 % heard) → Game.complete('mic').
   Portrait phone: the stage on top, Dock.karaoke (hook lines styled as the hook) + a device note + ↺ ▶ ⌂ + the strip.
   Long audio (docs/WEBSITE.md §8.4): lesson.json preload.stream names the .m4a, so it plays through the shared <audio>
   element (Audio.media), never decoded into memory.

   QA hooks: sc.song (S(i), E(i), W(i, word), beats), sc.plan (acts, marks, tStamp), sc.debugAt(t), sc.now(), sc.play(t),
   sc.pause(), sc.toggle(), sc.seek(t), sc.restart(), sc.done() / sc.finishStub(), sc.canFinish(), sc.lyricAt(t),
   sc.activeWord(t), sc.actAt(t), sc.marksAt(t) (the highlighter spans showing in the karaoke at t), sc.heard, sc.ended,
   sc.audioReady; Levels.mic.MARKS, Levels.mic.isHook(text).
   ========================================================================== */
(function () {
  const STEP = 'mic';
  const TAKE_NAME = 'spokenword_alt_7';
  const AUDIO = 'music/mic.m4a', WORDS = 'music/mic_words.json';
  const KEY = 'song:mic';
  const TYPE = "'Special Elite'", HANDF = "'Caveat Brush'";
  const Z = () => window.Zine;
  const ZC = () => (window.Zine ? Zine.C : { ink: '#141210', paper: '#f7f5ef', red: '#d9352c', grey: '#6d6a64', white: '#fffefa' });
  const COL = id => (window.Devices ? Devices.colour(id) : '#fff04a');
  const PARK = '#2b4a8c';
  const BAND_Y = 832, CUR_Y = 916, NEXT_Y = 1012, CUR_SIZE = 54, NEXT_SIZE = 34;
  const FADE = 0.35;                                  // act cross-fade (s)
  const THRESH = 0.9;                                 // share of the piece heard for the stamp / "Finish"
  const START_DELAY = 0.7;                            // the mic tap, then the piece
  const CREDIT = 'AI-generated performance';
  const HOOK_PAL = [['#141210', '#f7f5ef'], ['#f7f5ef', '#141210'], ['#fff04a', '#141210'], ['news', '#141210'], ['#ff6fbf', '#141210'], ['#141210', '#fff04a'], ['#72f06e', '#141210']];

  /* the board (a photocopied page taped up behind the performer) and the cast */
  const BD = { x: 1185, y: 420, w: 820, h: 560 };
  const BL = BD.x - BD.w / 2 + 60;                    // the board's text column
  const NIA = { x: 560, y: 772, h: 600 }, TABLE = { x: 712, top: 478 }, PK = { x: 1792, y: 836, h: 560 };
  const STAGE = { x: 600, y: 770, w: 1000 };

  /* the karaoke's device marks (content/mic.json lines; DESIGN.md §2 colours): the words are found in the line */
  const MARKS = [
    { dev: 'simile', phrase: 'as loud as a stadium' },
    { strike: true, phrase: 'like', inLine: 'I like pizza', at: "isn't", note: '"like" here is just a verb: "I like pizza" is not a simile' },
    { dev: 'metaphor', phrase: 'No like, no as, it just is' },
    { dev: 'hyperbole', phrase: 'weighs a ton' },
    { dev: 'personification', phrase: 'wind whispered' },
    { dev: 'idiom', phrase: 'Break a leg' },
  ];
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  const HOOKS = ['not a lie with extra steps', 'the truth in better shoes'].map(s => s.split(' ').map(normW).join(' '));
  const isHook = text => { const n = String(text).split(/\s+/).map(normW).filter(Boolean).join(' '); return HOOKS.some(h => n === h || n.endsWith(' ' + h)); };

  /* ---------------- song timing ---------------- */
  class Song {
    constructor(d) {
      this.d = d; this.lines = d.lines; this.dur = d.duration || 86;
      const b = d.beats || {};
      this.bpm = b.bpm || 85; this.bt = 60 / this.bpm; this.off = b.offset || 0;
      // per line: its device spans, struck words, hook flag
      this.lines.forEach(l => {
        const ws = l.words.map(w => normW(w.w));
        l.marks = []; l.strike = []; l.hook = isHook(l.text);
        MARKS.forEach((m, mi) => {
          if (m.inLine && !l.text.includes(m.inLine)) return;
          const ph = m.phrase.split(/\s+/).map(normW);
          for (let k = 0; k + ph.length <= ws.length; k++) {
            if (!ph.every((p, j) => ws[k + j] === p)) continue;
            if (m.strike) { const a = m.at ? ws.indexOf(normW(m.at), k) : -1; l.strike.push({ j: k, mi, note: m.note, t: a >= 0 ? l.words[a].s : l.words[k].e }); }
            else l.marks.push({ dev: m.dev, j0: k, j1: k + ph.length - 1, mi });
            break;
          }
        });
      });
      this.items = d.lines.map(l => ({ ...l, kind: 'line' })).concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j, marks: [], strike: [], hook: false }))).sort((a, c) => a.s - c.s);
    }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) return 1e6;
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    WE(i, word, n = 1) {                              // the end of that word
      const l = this.lines[i]; if (!l) return 1e6;
      const s = this.W(i, word, n), w = l.words.find(x => x.s === s);
      return w ? w.e : s + 0.3;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    pulse(t, k = 9) { return t < this.off - 0.05 ? 0 : Math.exp(-this.sinceBeat(t) * k); }
    speaking(t) { return this.lines.some(l => t >= l.s - 0.05 && t <= l.e + 0.1); }
  }

  /* ---------------- the plan: acts keyed to line starts ---------------- */
  function plan(S) {
    const b = i => S.S(i) - 0.3;
    const acts = [
      { id: 'intro', at: 0, draw: actIntro },
      { id: 'simile', at: b(1), draw: actSimile },
      { id: 'notsimile', at: b(3), draw: actNotSimile },
      { id: 'chorus1', at: b(5), draw: actChorus, c0: 5 },
      { id: 'hyperbole', at: b(9), draw: actHyperbole },
      { id: 'personification', at: b(11), draw: actPerson },
      { id: 'idiom', at: b(12), draw: actIdiom },
      { id: 'chorus2', at: b(13), draw: actChorus, c0: 13 },
      { id: 'outro', at: b(17), draw: actOutro },
    ];
    acts.forEach((a, i) => { a.end = i + 1 < acts.length ? acts[i + 1].at : S.dur + 1; });
    // the board steps aside for the hook's cut-out letters
    const hooks = [[S.S(7), b(9)], [S.S(15), b(17)], [b(17), S.E(18) + 0.6]];
    // the moments Nia points at the board (a device is named)
    const points = [S.W(1, 'simile'), S.W(3, 'one'), S.W(5, 'sneakier'), S.W(10, 'hyperbole'), S.W(11, 'person'), S.W(12, 'means'), S.W(13, 'sneakier')];
    return { acts, ticks: acts.slice(1).map(a => a.at), hooks, points, tStamp: S.E(18) + 0.9 };
  }
  function actAt(p, t) { let k = 0; for (let i = 0; i < p.acts.length; i++) if (t >= p.acts[i].at) k = i; return k; }
  function hookness(p, t) { let h = 0; for (const [a, b] of p.hooks) if (t >= a && t < b + FADE) h = Math.max(h, P(t, a, a + FADE) * (1 - P(t, b, b + FADE))); return h; }

  /* ---------------- drawing helpers ---------------- */
  const layoutCache = new Map();
  const fontOk = () => !document.fonts || document.fonts.status === 'loaded';
  function measure(str, font) {
    const k = font + '|' + str;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  function ransomW(str, size, seed) {
    const k = 'R|' + str + '|' + size + '|' + seed;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); w = ransom(str, -9999, -9999, size, 1e9, { seed, palette: HOOK_PAL }); g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  const inkA = (pts, o = {}) => ink(pts, { ...o, alpha: (o.alpha ?? 1) * g.globalAlpha });
  function withAlpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  /* words j0..j1 of line i typed on at their spoken times, left-aligned from x; returns [{x0, x1}] per word */
  function typeWords(S, t, i, j0, j1, x, y, size, o = {}) {
    const l = S.lines[i], out = []; if (!l) return out;
    const Zn = Z(), sp = Zn.measure(' ', size);
    let cx = x;
    for (let j = j0; j <= Math.min(j1, l.words.length - 1); j++) {
      const w = l.words[j], txt = o.upper ? w.w.toUpperCase() : w.w, ww = Zn.measure(txt, size);
      out.push({ x0: cx, x1: cx + ww, s: w.s, e: w.e });
      if (!o.dry && t >= w.s) Zn.type(txt, cx, y, size, { t0: w.s, cps: Math.max(18, txt.length / Math.max(0.12, (w.e - w.s) * 0.8)), seed: (o.seed || 1) + j, color: o.color });
      cx += ww + sp;
    }
    return out;
  }
  /* a highlighter swipe under words [x0..x1], drawn on from the first word's start to the last word's end */
  function swipe(ws, y, size, colour, t, seed) {
    if (!ws.length || t < ws[0].s) return;
    const a = ws[0], b = ws[ws.length - 1];
    Z().highlight(a.x0 - 10, y + 2, b.x1 - a.x0 + 20, size * 1.1, colour, { prog: E.outQuad(P(t, a.s - 0.05, Math.max(a.s + 0.2, b.e))), seed });
  }
  /* a red ink strike through [x0, x1] at y */
  function strike(x0, x1, y, size, t, t0, seed) {
    if (t < t0) return;
    inkA([[x0 - 8, y + size * 0.08], [x1 + 8, y - size * 0.12]], { w: Math.max(5, size * 0.1), color: ZC().red, seed, prog: E.outCubic(P(t, t0, t0 + 0.25)), passes: 2, step: 12 });
  }
  const chip = (id, x, y, t, t0, o = {}) => { if (t >= t0) Z().label(id, x, y, { size: o.size || 34, rot: o.rot || 0, s: pop(t, t0, 0.35, 2.2), seed: o.seed || 1, text: o.text || null }); };

  /* ---------------- the stage ---------------- */
  const SNAPS = [[120, 400], [300, 250], [1650, 190], [1300, 760], [340, 560], [1555, 760], [90, 180], [990, 750]];
  function backdrop(S, p, t, docked) {
    const Zn = Z();
    g.fillStyle = paperTile('#2c2724', 51, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
    const sp = Zn.speckle(960, 540, 52, 0.9); g.save(); g.globalCompositeOperation = 'screen'; g.globalAlpha = 0.22; g.drawImage(sp, 0, 0, 1920, 1080); g.restore();
    for (let k = 0; k < 3; k++) {
      paperRect(30 + k * 58, 470, 68, 1000, k % 2 ? '#6e1f1b' : '#842723', { seed: 601 + k, depth: 0.8, boil: false });
      paperRect(1890 - k * 58, 470, 68, 1000, k % 2 ? '#6e1f1b' : '#842723', { seed: 611 + k, depth: 0.8, boil: false });
    }
    Zn.stage(STAGE.x, STAGE.y, STAGE.w, { seed: 620 });
    // the spotlight lamp on its tripod at stage left (the cut-out aims up-right, at Nia) + its beam
    const pu = S.pulse(t, 5);
    g.save(); g.globalCompositeOperation = 'lighter';
    const gr = g.createLinearGradient(250, 560, NIA.x, 300);
    gr.addColorStop(0, `rgba(255,246,210,${0.2 + 0.04 * pu})`); gr.addColorStop(1, 'rgba(255,246,210,0.02)');
    g.fillStyle = gr; g.beginPath(); g.moveTo(236, 548); g.lineTo(NIA.x + 170, 150); g.lineTo(NIA.x + 120, 640); g.lineTo(262, 590); g.closePath(); g.fill();
    g.restore();
    Zn.art('spotlight', 200, STAGE.y + 6, 250, { seed: 621 });
    if (docked) return;
  }
  function snaps(S, t) {
    const Zn = Z(), bp = S.beatPos(t), fl = Math.floor(bp);
    if (t < S.off || fl % 2 !== 1) return;                 // the backbeat (beats 2 and 4)
    const since = S.sinceBeat(t), k = Math.floor(hash1(fl * 7.13) * SNAPS.length), [x, y] = SNAPS[k];
    const s = pop(since, 0, 0.12, 2.2) * (1 - P(since, S.bt * 0.55, S.bt * 0.95));
    if (s <= 0.01) return;
    g.save(); g.translate(x, y); g.rotate((hash1(fl * 3.7) - 0.5) * 0.4); g.scale(s, s);
    Zn.doodle('onomatopoeia', 0, 0, 30, { color: '#f7f5ef', seed: 640 + (fl % 5), w: 3.5 });
    Zn.type('snap', 0, 48, 24, { align: 'center', color: '#f7f5ef', seed: 650 + (fl % 5) });
    g.restore();
  }
  function cast(S, p, t) {
    const speaking = S.speaking(t), pu = S.pulse(t, 7);
    const env = speaking ? 0.3 + 0.5 * pu : 0.12 * pu;
    const pointing = p.points.some(a => t >= a && t < a + 1.6);
    Puppet.draw('nia', NIA.x, NIA.y, NIA.h, { facing: 1, t, env, pose: pointing ? 'point' : 'nia' });
    // the tall table with the desk mic, in front of her
    paperRect(TABLE.x, (TABLE.top + STAGE.y) / 2 + 6, 26, STAGE.y - TABLE.top, '#3a3431', { seed: 661, depth: 0.8, boil: false });
    paperRect(TABLE.x, STAGE.y - 6, 120, 16, '#3a3431', { seed: 662, depth: 0.6, boil: false });
    paperRect(TABLE.x, TABLE.top, 170, 22, '#4a4440', { seed: 663, depth: 0.9, boil: false });
    Z().art('microphone_stand', TABLE.x + 6, TABLE.top - 8, 200, { seed: 664 });
    // Ms. Park hosting at the right (her own mic); pen raised when the piece is published
    const end = t >= p.tStamp;
    Puppet.draw('park', PK.x, PK.y, PK.h, { facing: -1, t, env: 0.08 * pu, pose: end ? 'point' : 'park' });
  }
  /* the board page (fades out while the hook's letters are up) */
  function board(p, t) {
    const a = 1 - hookness(p, t);
    withAlpha(a, () => { Z().page(BD.x, BD.y, BD.w, BD.h, { seed: 671, rot: -0.008, tape: 2, dark: 0.45 }); });
    return a;
  }

  /* ======================================================================
     the acts (on the board)
     ====================================================================== */
  function actIntro(S, t) {
    const Zn = Z(), C = ZC();
    Zn.type('OPEN MIC', BL, BD.y - 210, 44, { t0: -1e6, seed: 701 });
    Zn.type('tonight: Nia Brooks', BL + 250, BD.y - 208, 30, { t0: -1e6, color: C.grey, seed: 702 });
    typeWords(S, t, 0, 0, 2, BL, BD.y - 110, 40, { seed: 710 });
    typeWords(S, t, 0, 3, 4, BL, BD.y - 20, 60, { seed: 720, upper: true });
    typeWords(S, t, 0, 5, 7, BL, BD.y + 70, 60, { seed: 730, upper: true });
    Zn.art('typewriter', BD.x + 250, BD.y + BD.h / 2 - 30, 190, { seed: 741, s: 1 });
  }

  function actSimile(S, t) {
    const Zn = Z(), C = ZC();
    typeWords(S, t, 1, 0, 2, BL, BD.y - 190, 50, { seed: 800 });
    const r2 = typeWords(S, t, 1, 3, 7, BL, BD.y - 115, 50, { dry: true });
    swipe(r2, BD.y - 115, 50, COL('simile'), t, 811);
    typeWords(S, t, 1, 3, 7, BL, BD.y - 115, 50, { seed: 810 });          // the words over the ink
    chip('simile', BD.x + 210, BD.y - 20, t, S.W(1, 'simile'), { rot: -0.04, seed: 812 });
    // comparing two things: HALLWAY ≈ STADIUM (like or as)
    const t2 = S.W(2, 'two'), tl = S.W(2, 'like'), ta = S.W(2, 'as');
    if (t >= t2) {
      Zn.type('HALLWAY', BD.x - 250, BD.y + 110, 50, { t0: t2, cps: 30, align: 'center', seed: 820 });
      Zn.type('STADIUM', BD.x + 190, BD.y + 110, 50, { t0: t2 + 0.2, cps: 30, align: 'center', seed: 821 });
    }
    if (t >= tl) Zn.simileMark(BD.x - 30, BD.y + 108, 90, { seed: 822, prog: P(t, tl, tl + 0.4), lw: 7 });
    if (t >= tl) hand('like', BD.x - 70, BD.y + 190, 46, tl, { color: C.ink, seed: 823, dur: 0.2 });
    if (t >= ta) hand('or as', BD.x + 20, BD.y + 190, 46, ta, { color: C.ink, seed: 824, dur: 0.2 });
    if (t >= S.W(2, 'obvious')) hand('two things, compared', BD.x - 30, BD.y + 245, 40, S.W(2, 'obvious'), { color: C.red, seed: 825, dur: 0.4 });
  }

  function actNotSimile(S, t) {
    const Zn = Z(), C = ZC();
    const ws = typeWords(S, t, 3, 1, 3, BL + 10, BD.y - 175, 70, { seed: 900 });
    const lk = ws[1];
    if (lk) strike(lk.x0, lk.x1, BD.y - 175, 70, t, S.W(3, 'isnt'), 901);
    chip('none', BD.x + 170, BD.y - 80, t, S.W(3, 'one'), { rot: 0.04, seed: 902, text: 'NOT A SIMILE', size: 32 });
    if (t >= S.W(3, 'verb')) hand('"like" = just a verb', BL + 40, BD.y - 80, 44, S.W(3, 'verb'), { align: 'left', color: C.red, seed: 903, dur: 0.4 });
    // what to look for
    const t4 = S.W(4, 'Look'), tt = S.W(4, 'two'), tc = S.W(4, 'compared'), ts = S.W(4, 'single');
    if (t >= t4) Zn.type('LOOK FOR:', BL, BD.y + 20, 40, { t0: t4, cps: 30, seed: 910 });
    if (t >= tt) { Zn.type('1. two things', BL + 40, BD.y + 85, 40, { t0: tt, cps: 30, seed: 911 }); Zn.doodle('simile', BL + 420, BD.y + 85, 26, { t0: tt + 0.3, seed: 912 }); }
    if (t >= tc) Zn.type('2. being compared', BL + 40, BD.y + 145, 40, { t0: tc, cps: 30, seed: 913 });
    if (t >= ts) {
      Zn.type('not just the word "like"', BL + 40, BD.y + 205, 40, { t0: ts, cps: 30, color: C.grey, seed: 914 });
      Zn.art('cat', BD.x + 270, BD.y + BD.h / 2 - 20, 170, { seed: 915, s: pop(t, ts, 0.35, 1.8) });
    }
  }

  /* the chorus: "Like a simile, only sneakier" = METAPHOR; LIKE / AS struck, IS in pink; then the hook letters */
  function actChorus(S, t, A, p) {
    const c0 = A.c0, Zn = Z(), C = ZC();
    const bA = 1 - hookness(p, t);
    withAlpha(bA, () => {
      typeWords(S, t, c0, 0, 2, BL, BD.y - 190, 54, { seed: 1000 + c0 });
      typeWords(S, t, c0, 3, 4, BL, BD.y - 115, 54, { seed: 1010 + c0 });
      chip('metaphor', BD.x + 180, BD.y - 150, t, S.W(c0, 'sneakier'), { rot: 0.05, seed: 1020 + c0 });
      const l1 = c0 + 1, tl = S.W(l1, 'like'), ta = S.W(l1, 'as'), ti = S.W(l1, 'is');
      if (t >= tl) { Zn.type('LIKE', BD.x - 280, BD.y + 60, 72, { t0: tl, cps: 30, align: 'center', seed: 1030 }); strike(BD.x - 360, BD.x - 200, BD.y + 60, 72, t, tl + 0.3, 1031); }
      if (t >= ta) { Zn.type('AS', BD.x - 60, BD.y + 60, 72, { t0: ta, cps: 30, align: 'center', seed: 1032 }); strike(BD.x - 110, BD.x - 10, BD.y + 60, 72, t, ta + 0.25, 1033); }
      if (t >= ti) {
        Zn.highlight(BD.x + 110, BD.y + 62, 170, 100, COL('metaphor'), { t0: ti, dur: 0.3, seed: 1034 });
        const s = 1 + 0.15 * Math.exp(-(t - ti) * 6);
        g.save(); g.translate(BD.x + 195, BD.y + 60); g.scale(s, s); Zn.type('IS', 0, 0, 96, { t0: ti, cps: 30, align: 'center', seed: 1035 }); g.restore();
        hand('it just IS the other thing', BD.x, BD.y + 170, 44, ti + 0.2, { color: C.red, seed: 1036, dur: 0.5 });
      }
    });
    hookLetters(S, t, c0 + 2, c0 + 3, 1 - P(t, A.end - 0.2, A.end + 0.1));
  }
  /* the hook's two lines as big cut-out letters over the stage (two rows each), word by word */
  function rowsOf(words) {
    const n = words.length; let best = 1, bd = 1e9;
    for (let k = 1; k < n; k++) { const a = words.slice(0, k).join(' ').length, b = words.slice(k).join(' ').length; if (Math.abs(a - b) < bd) { bd = Math.abs(a - b); best = k; } }
    return [words.slice(0, best), words.slice(best)];
  }
  function hookLetters(S, t, la, lb, alpha = 1) {
    if (t < S.S(la) - 0.05 || alpha <= 0.001) return;
    const pu = S.pulse(t, 7);
    withAlpha(alpha, () => [la, lb].forEach((li, n) => {
      const l = S.lines[li]; if (!l || t < l.s - 0.05) return;
      const W = l.words.map(w => w.w.replace(/[,.!?]+$/, '').toUpperCase());
      const rows = rowsOf(W.map((w, j) => j));
      const seedB = 1200 + li * 17;
      // one size for the line: the widest row fits the board's width
      const fit = Math.min(92, ...rows.map(r => 100 * (BD.w + 60) / ransomW(r.map(j => W[j]).join(' '), 100, seedB)));
      rows.forEach((row, r) => {
        const y0 = (n ? 470 : 200) + r * fit * 1.3, gap = fit * 0.42;
        const wsz = row.map(j => ransomW(W[j], fit, seedB + j));
        const tot = wsz.reduce((a, c) => a + c, 0) + gap * (row.length - 1);
        let x = BD.x - tot / 2;
        row.forEach((j, q) => {
          const w = l.words[j], cx = x + wsz[q] / 2; x += wsz[q] + gap;
          if (t < w.s) return;
          const s = 1 + 0.04 * pu + 0.14 * Math.exp(-(t - w.s) * 7);
          g.save(); g.translate(cx, y0); g.scale(s, s);
          ransom(W[j], 0, 0, fit, w.s, { seed: seedB + j, stagger: 0.02, palette: HOOK_PAL });
          g.restore();
        });
      });
    }));
  }

  function actHyperbole(S, t) {
    const Zn = Z(), C = ZC();
    const ws = typeWords(S, t, 9, 0, 4, BL, BD.y - 190, 50, { dry: true });
    swipe(ws.slice(2), BD.y - 190, 50, COL('hyperbole'), t, 1301);
    typeWords(S, t, 9, 0, 4, BL, BD.y - 190, 50, { seed: 1300 });
    // a 1 TON weight drops onto the page
    const tt = S.W(9, 'ton');
    if (t >= tt - 0.25) {
      const u = P(t, tt - 0.25, tt), y = lerp(BD.y - 420, BD.y + 60, u * u), sq = t > tt ? 1 - 0.12 * Math.exp(-(t - tt) * 9) * Math.cos((t - tt) * 30) : 1;
      g.save(); g.translate(BD.x - 200, y); g.scale(1 / sq, sq);
      paperPiece(TON, 0, 0, '#3a3431', { seed: 1302, depth: 1.2 });
      Zn.type('1 TON', 0, 18, 46, { align: 'center', color: '#f7f5ef', seed: 1303 });
      g.restore();
    }
    if (t >= S.W(9, 'thinks')) hand('nobody believes it', BD.x - 200, BD.y + 180, 42, S.W(9, 'thinks'), { color: C.red, seed: 1304, dur: 0.4 });
    chip('hyperbole', BD.x + 190, BD.y - 60, t, S.W(10, 'hyperbole'), { rot: -0.04, seed: 1305 });
    const tb = S.W(10, 'big');
    if (t >= tb) {
      const s = 0.5 + 0.7 * E.outBack(P(t, tb, tb + 0.5), 2);
      g.save(); g.translate(BD.x + 190, BD.y + 70); g.scale(s, s); ransom('BIG', 0, 0, 80, tb, { seed: 1306, palette: HOOK_PAL }); g.restore();
    }
    if (t >= S.W(10, 'purpose')) hand('on purpose', BD.x + 190, BD.y + 170, 46, S.W(10, 'purpose'), { color: C.ink, seed: 1307, dur: 0.35 });
  }
  const TON = Object.assign([[-120, 70], [120, 70], [80, -40], [30, -40], [30, -70], [-30, -70], [-30, -40], [-80, -40]], { k: 'mic:ton' });

  function actPerson(S, t) {
    const Zn = Z(), C = ZC();
    const ws = typeWords(S, t, 11, 0, 5, BL, BD.y - 190, 46, { dry: true });
    swipe(ws.slice(1, 3), BD.y - 190, 46, COL('personification'), t, 1401);
    typeWords(S, t, 11, 0, 5, BL, BD.y - 190, 46, { seed: 1400 });
    const tw = S.W(11, 'wind'), th = S.W(11, 'whispered');
    if (t >= tw) {
      const cx = BD.x - 160, cy = BD.y + 60, sway = Math.sin(t * 2.2) * 6;
      Zn.art('cloud', cx + sway, cy, 190, { ay: 0.5, seed: 1402, s: pop(t, tw, 0.35, 1.8) });
      if (t >= th) {
        Zn.doodle('personification', cx + sway - 10, cy + 4, 58, { t0: th, dur: 0.45, seed: 1403, w: 7 });
        for (let k = 0; k < 3; k++) {
          const y = cy - 40 + k * 44, x0 = cx + 150, pts = [];
          for (let i = 0; i <= 16; i++) { const u = i / 16; pts.push([x0 + u * 220, y + Math.sin(u * TAU * 1.5 + t * 3 + k) * 10]); }
          inkA(pts, { w: 4, color: C.ink, seed: 1404 + k, prog: E.outCubic(P(t, th + k * 0.12, th + 0.5 + k * 0.12)), passes: 1, step: 10 });
        }
        hand('"shh"', cx + 300, cy - 88, 44, th + 0.3, { color: C.grey, seed: 1408, dur: 0.3 });
      }
    }
    chip('personification', BD.x + 150, BD.y + 205, t, S.W(11, 'person'), { rot: 0.03, seed: 1409, size: 30 });
    if (t >= S.W(11, 'air')) hand("wind can't really whisper", BD.x - 60, BD.y + 150, 38, S.W(11, 'air'), { color: C.red, seed: 1410, dur: 0.4 });
  }

  function actIdiom(S, t) {
    const Zn = Z(), C = ZC();
    const ws = typeWords(S, t, 12, 0, 2, BL, BD.y - 170, 72, { dry: true });
    swipe(ws, BD.y - 170, 72, COL('idiom'), t, 1501);
    typeWords(S, t, 12, 0, 2, BL, BD.y - 170, 72, { seed: 1500 });
    const tm = S.W(12, 'means');
    if (t >= tm) hand('= good luck!', BL + 40, BD.y - 50, 76, tm, { align: 'left', color: C.red, seed: 1502, dur: 0.5 });
    chip('idiom', BD.x + 170, BD.y + 60, t, S.W(12, 'luck'), { rot: -0.03, seed: 1503 });
    if (t >= S.W(12, 'breaks')) {
      Zn.type('learned whole,', BL + 20, BD.y + 150, 38, { t0: S.W(12, 'breaks'), cps: 34, seed: 1504 });
      Zn.type('not word by word', BL + 20, BD.y + 200, 38, { t0: S.W(12, 'breaks') + 0.4, cps: 34, seed: 1505 });
    }
  }

  function actOutro(S, t, A, p) {
    const tS = p.tStamp, Zn = Z(), C = ZC();
    hookLetters(S, t, 17, 18, 1 - P(t, S.E(18) + 0.3, S.E(18) + 0.6));
    // the board comes back as Nia's zine page: the title, her byline, then PUBLISHED
    if (t >= S.E(18) + 0.3) withAlpha(P(t, S.E(18) + 0.3, S.E(18) + 0.7), () => {
      const sz = Math.min(80, 100 * (BD.w - 120) / ransomW('ONLY SNEAKIER', 100, 1702));
      ransom('LIKE A SIMILE,', BD.x, BD.y - 150, sz, S.E(18) + 0.4, { seed: 1701, palette: HOOK_PAL });
      ransom('ONLY SNEAKIER', BD.x, BD.y - 150 + sz * 1.3, sz, S.E(18) + 0.55, { seed: 1702, palette: HOOK_PAL });
      Zn.type('a spoken-word piece by Nia Brooks', BD.x, BD.y + 225, 34, { t0: S.E(18) + 0.6, cps: 60, align: 'center', color: C.grey, seed: 1703 });
    });
    if (t >= tS) {
      // the snaps: a burst all round
      SNAPS.forEach(([x, y], i) => {
        const a = tS + i * 0.1, s = pop(t, a, 0.25, 2.2) * (1 - P(t, a + 1.6, a + 2.0));
        if (s <= 0.01) return;
        g.save(); g.translate(x, y); g.rotate((i % 3 - 1) * 0.15); g.scale(s, s);
        Z().doodle('onomatopoeia', 0, 0, 36, { color: '#f7f5ef', seed: 1600 + i, w: 4 });
        Z().type('snap!', 0, 56, 28, { align: 'center', color: '#f7f5ef', seed: 1610 + i });
        g.restore();
      });
      Z().stamp('PUBLISHED', BD.x + 60, BD.y + 60, { rot: -0.12, s: pop(t, tS, 0.3, 2.2) * 1.1 });
    }
  }

  function drawStage(S, p, t, docked) {
    backdrop(S, p, t, docked);
    board(p, t);
    const k = actAt(p, t), A = p.acts[k];
    const run = (i, a) => withAlpha(a, () => { g.save(); const X = p.acts[i]; if (X.id === 'outro' || X.id.startsWith('chorus')) X.draw(S, t, X, p); else withAlpha(1 - hookness(p, t), () => X.draw(S, t, X, p)); g.restore(); });
    if (k > 0 && t < A.at + FADE) run(k - 1, 1 - P(t, A.at, A.at + FADE));
    run(k, k === 0 ? 1 : P(t, A.at, A.at + FADE * 0.6));
    cast(S, p, t);
    snaps(S, t);
  }

  /* ---------------- karaoke ---------------- */
  function markOf(l, j) { return l.marks.find(m => j >= m.j0 && j <= m.j1) || null; }
  const lays = new Map();
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (lays.has(key)) return lays.get(key);
    const f = `${size}px ${TYPE}`, space = measure(' ', f) + size * 0.1, rs = size * 0.78;
    let x = 0;
    const toks = item.words.map((w, j) => {
      const tk = { text: w.w, s: w.s, e: w.e, j, seed: 400 + item.i * 13 + j * 5 };
      if (item.hook) { tk.up = w.w.replace(/[,.!?:]+$/, '').toUpperCase(); tk.trail = (w.w.match(/[,.!?:]+$/) || [''])[0]; tk.w = ransomW(tk.up, rs, tk.seed); tk.x = x; x += tk.w + (tk.trail ? measure(tk.trail, f) : 0) + space + size * 0.15; }
      else { tk.w = measure(w.w, f); tk.x = x; x += tk.w + space; }
      return tk;
    });
    const out = { toks, width: x - space, size, rs, f };
    if (fontOk()) lays.set(key, out);
    return out;
  }
  const tokEnd = (toks, j) => { const tk = toks[j], nx = toks[j + 1]; return Math.min(Math.max(tk.e, tk.s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  function drawCurrent(item, t, y, maxW = 1560) {
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 900 - Lo.width / 2, C = ZC(), Zn = Z();
    const appear = item.appear ?? item.s - 0.5, a = E.outCubic(P(t, appear, appear + 0.25)), yy = y + (1 - a) * 30;
    // the device highlighter under its words, drawn on as they're said
    item.marks.forEach((m, q) => {
      const A = Lo.toks[m.j0], B = Lo.toks[m.j1];
      if (t < A.s - 0.05) return;
      Zn.highlight(x0 + A.x - 10, yy + size * 0.04, B.x + B.w - A.x + 20, size * 1.12, COL(m.dev), { prog: E.outQuad(P(t, A.s - 0.05, Math.max(A.s + 0.2, B.e))), seed: 90 + item.i * 3 + q });
    });
    Lo.toks.forEach((tk, j) => {
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < tokEnd(Lo.toks, j);
      if (item.hook) {
        if (!sung) withAlpha(0.22 * a, () => { g.font = `${Math.round(size * 0.9)}px ${TYPE}`; g.fillStyle = C.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(tk.up, x + tk.w / 2, yy); });
        else {
          const beat = 1 + 0.08 * Math.exp(-(t - tk.s) * 6);
          g.save(); g.translate(x + tk.w / 2, yy); g.scale(beat, beat);
          ransom(tk.up, 0, 0, Lo.rs, tk.s, { seed: tk.seed, stagger: clamp((tk.e - tk.s) / Math.max(1, tk.up.length), 0.015, 0.05), palette: HOOK_PAL });
          g.restore();
          if (tk.trail) { g.save(); g.font = Lo.f; g.fillStyle = C.ink; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(tk.trail, x + tk.w + 4, yy); g.restore(); }
        }
        return;
      }
      g.save(); g.font = Lo.f; g.textAlign = 'center'; g.textBaseline = 'middle';
      const cx = x + tk.w / 2;
      if (!sung) { g.globalAlpha *= 0.22 * a; g.fillStyle = C.ink; g.fillText(tk.text, cx, yy); }
      else {
        // the word STAMPS on: big and light → its size, full ink (a typewriter strike)
        const k = Math.exp(-(t - tk.s) * 16), s = 1 + 0.35 * k;
        g.translate(cx, yy + size * 0.04 * hash1(tk.seed)); g.rotate((hash1(tk.seed + 1) - 0.5) * 0.04); g.scale(s, s);
        g.globalAlpha *= 1 - 0.5 * k;
        g.fillStyle = active ? C.red : C.ink; g.fillText(tk.text, 0, 0);
      }
      g.restore();
      const st = item.strike.find(q => q.j === j);
      if (st && t >= st.t) strike(x, x + tk.w, yy, size, t, st.t, 95 + j);
    });
    return size;
  }
  /* a line comes up 0.5 s before its first word, but never before the previous line has been spoken */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.5 : Math.min(it[k].s, Math.max(it[k].s - 0.5, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) { const nx = ws[j + 1], end = Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); if (t >= ws[j].s - 0.02 && t < end) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e }; }
    return null;
  }
  /* the device the current line shows (for the chip / the dock note), once its first word is said */
  function deviceNote(cur, t) {
    if (!cur) return null;
    const m = cur.marks.find(x => t >= cur.words[x.j0].s - 0.05);
    if (m) return { dev: m.dev, text: `${Devices.name(m.dev)}: "${cur.words.slice(m.j0, m.j1 + 1).map(w => w.w).join(' ').replace(/[,.!?]+$/, '')}"` };
    const st = cur.strike.find(x => t >= x.t);
    if (st) return { dev: 'none', text: st.note, label: 'NOT A SIMILE' };
    return null;
  }
  function band(song, t) {
    const C = ZC(), Zn = Z();
    Zn.page(960, BAND_Y + 140, 2040, 290, { seed: 77, dark: 0.3 });
    tape(110, BAND_Y + 6, 150, -0.08, undefined, 71); tape(1810, BAND_Y + 6, 150, 0.08, undefined, 72);
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) Zn.type(sec.toLowerCase(), 70, BAND_Y + 36, 26, { color: C.grey, seed: 73 });
    const dn = deviceNote(cur, t);
    if (dn) {
      const at = dn.dev === 'none' ? cur.strike[0].t : cur.words[cur.marks[0].j0].s;
      const lt = dn.label || Devices.name(dn.dev).toUpperCase(), lw = Zn.measure(lt, 26) + 26 * 2.6;
      Zn.label(dn.dev, 1880 - lw / 2, BAND_Y + 26, { size: 26, rot: 0.02, s: pop(t, at, 0.3, 2), seed: 74, text: dn.label || null });
    }
    if (cur) drawCurrent(cur, t, CUR_Y);
    const note = (txt, col, a) => { let size = NEXT_SIZE; const f = s => `${s}px ${TYPE}`; const w = measure(txt, f(size)); if (w > 1700) size = Math.floor(size * 1700 / w);
      g.save(); g.globalAlpha *= a; g.font = f(size); g.fillStyle = col; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(txt, 960, NEXT_Y); g.restore(); };
    if (next) note(next.text, C.grey, 0.8);
    else if (song.items.length && t > song.items[song.items.length - 1].e) note('Not a lie. The truth in better shoes.', PARK, 0.85);
    if (!cur && !next && t > 1) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      [0, 1, 2].forEach(i => Zn.doodle('onomatopoeia', 860 + i * 100, CUR_Y - 20 * pu * (i === b ? 1 : 0.15), 22, { seed: 80 + i, color: C.ink, w: 3 }));
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class Mic extends Scene {
    enter() {
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false; this.startAt = null;
      this.pos = 0; this.t0ctx = null; this.heard = 0; this.lastT = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      if (!window.Devices) this.err = 'the device model is missing';
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { this.song = new Song(d); this.plan = plan(this.song); })
        .catch(e => { this.err = String(e && e.message); console.warn('[mic] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); });
      Promise.all([words, audio]).then(() => {
        if (this.gone || this.frozen || this.startedOnce || !this.song) return;
        // the mic tap, then the piece
        if (Audio.has('mic_tap') && !(window.__timeScale > 1)) Audio.play('mic_tap', { gain: 0.5 });
        this.startAt = Game.time + (window.__timeScale > 1 ? 0 : START_DELAY);
      });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false; this.startAt = null;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.lastT = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.startAt = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.lastT = this.pos; this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; this.lastT = t; }
    /* stamp the step now (the end of a ≥ 90 % listen, the "Finish" button, QA) */
    done() { if (this.completed) return; this.completed = true; if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    finishStub() { this.done(); }
    canFinish() { return !!this.song && !this.completed && this.heard >= THRESH * this.song.dur; }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    actAt(t) { return this.plan ? this.plan.acts[actAt(this.plan, t)].id : null; }
    marksAt(t) {
      const c = this.song && lyricAt(this.song, t).cur; if (!c) return [];
      return c.marks.filter(m => t >= c.words[m.j0].s - 0.05).map(m => ({ line: c.i, dev: m.dev, colour: COL(m.dev), words: c.words.slice(m.j0, m.j1 + 1).map(w => w.w).join(' ') }))
      .concat(c.strike.filter(q => t >= q.t).map(q => ({ line: c.i, strike: c.words[q.j].w })));
    }

    update(dt) {
      if (this.startAt != null && !this.playing && Game.time >= this.startAt && !this.gone) this.play(0);
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      const now = this.now();
      if (this.lastT < this.plan.tStamp && now >= this.plan.tStamp) {
        Audio.play('stamp', { gain: 0.6 });
        if (Audio.has('snaps')) Audio.play('snaps', { gain: 0.55 });
      }
      this.lastT = now;
      if (now >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= THRESH * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) Game.replace(new Levels.map());
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this.finishRect && Math.abs(x - this.finishRect[0]) < this.finishRect[2] / 2 && Math.abs(y - this.finishRect[1]) < this.finishRect[3] / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null; this.lastT = this.pos;
      if (was) this.play(this.pos);
    }

    draw() {
      const docked = UI.docked(), title = 'Open Mic';
      if (!this.song || this.err) {
        g.fillStyle = paperTile('#2c2724', 51, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
        ransom('OPEN MIC', 960, 400, 110, this.enteredAt + 0.2, { seed: 42, palette: HOOK_PAL });
        const msg = this.err ? 'the piece is missing (' + this.err + ')' : 'tapping the mic…';
        hand(msg, 960, 580, 54, this.enteredAt + 0.4, { color: '#f7f5ef', seed: 44 });
        if (docked) { Dock.head(title, ''); Dock.text('mic-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try {
        drawStage(this.song, this.plan, t, docked);
        if (!docked) band(this.song, t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t, title); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, the device note, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) this._dk = { key, words: cur.words.map(w => ({ w: w.w, s: w.s, e: w.e, hook: !!cur.hook })) };
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? 'Not a lie. The truth in better shoes.' : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'tapping the mic…' });
      const dn = deviceNote(cur, t);
      Dock.text('mic-dev', [dn ? dn.text : ' '], { cls: 'small center' });
      Dock.text('credit', ['♪ ' + CREDIT], { cls: 'small center' });
      if (this.ended && Game.current() === this) {
        Dock.buttons('mic-end', [{ label: '↺ Play it again', color: PAL.teal, onClick: () => this.restart() },
          { label: 'Back to the map', color: PARK, onClick: () => Game.replace(new Levels.map()) }]);
      } else if (this.canFinish() && Game.current() === this) {
        Dock.buttons('mic-finish', [{ label: 'Finish', color: ZC().red, onClick: () => this.done() }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'position in the piece', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150, 1080);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the piece from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => Game.replace(new Levels.map()) });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos; this.lastT = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    /* the paused ▶: a dim wash and a highlighter-yellow paper disc */
    pausedMark(y, r, h) {
      g.save(); g.fillStyle = 'rgba(20,18,16,0.35)'; g.fillRect(0, 0, 1920, h); g.restore();
      paperCircle(960, y, r, '#fff04a', { seed: 63, depth: 1.6 });
      g.save(); g.fillStyle = '#141210'; g.beginPath(); g.moveTo(960 - r * 0.32, y - r * 0.55); g.lineTo(960 + r * 0.55, y); g.lineTo(960 - r * 0.32, y + r * 0.55); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const t0 = this.enteredAt, S = this.song, C = ZC();
      ransom('OPEN MIC', 40, 56, 36, t0 + 0.1, { align: 'left', seed: 61, palette: HOOK_PAL });
      hand('♪ ' + CREDIT, 44, 114, 28, t0 + 0.4, { align: 'left', color: '#f7f5ef', seed: 62, dur: 0.4 });
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx = { r: m.r, fixed: true };
      if (UI.iconButton('mic-map', '⌂', m.x, m.y, fx)) { Game.replace(new Levels.map()); return; }
      if (S) {
        if (UI.iconButton('mic-restart', '↺', r.x, r.y, fx)) this.restart();
        if (UI.iconButton('mic-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx, color: '#fff04a' })) this.toggle();
      }
      if (t == null) return;
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('mic-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 22 : 12, sy = 1080 - sh;
      g.save();
      g.fillStyle = 'rgba(20,18,16,0.25)'; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = C.red; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = C.ink; this.plan.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) paperCircle(clamp(1920 * t / S.dur, 26, 1894), touch ? 1080 - 24 : sy + sh / 2, touch ? 22 : 14, C.ink, { seed: 66, depth: 0.8, boil: false });
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        this.pausedMark(430, 100, BAND_Y);
        hand(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, 48, -1e6, { dur: 0.01, color: '#f7f5ef', outline: 'rgba(20,18,16,0.92)', seed: 64 });
      }
      if (!this.startedOnce && !this.frozen) hand('tapping the mic…', 960, 590, 48, this.enteredAt + 0.3, { color: '#f7f5ef', seed: 65 });
      this.finishRect = null;
      if (this.ended && Game.current() === this) {
        if (UI.button('mic-again', '↺ Play it again', 700, 640, { w: 440, h: 110, color: PAL.teal, size: 56 })) this.restart();
        if (UI.button('mic-back', 'Back to the map', 1220, 640, { w: 440, h: 110, color: PARK, size: 56 })) Game.replace(new Levels.map());
      } else if (this.canFinish() && Game.current() === this) {
        const bw = 300, bh = UI.tap(90), bx = 1920 - 40 - bw / 2, by = m.y + m.r + 28 + bh / 2;
        this.finishRect = [bx, by, bw, bh];
        if (UI.button('mic-finish', 'Finish', bx, by, { w: bw, h: 90, color: C.red, size: 52 })) this.done();
      }
    }
  }
  Mic.STEP = STEP;
  Mic.TAKE = TAKE_NAME;
  Mic.Song = Song;
  Mic.plan = plan;
  Mic.MARKS = MARKS;
  Mic.isHook = isHook;
  Levels.mic = Mic;
})();
